import React from "react";
import { BsArrowRight } from "react-icons/bs";
// import { useTheme } from "../layout";
import format from "date-fns/format";
import Link from "next/link";
import { getPostHref, getTopicHref } from "@/lib/contentRouting";
// import { PostsType } from "../../pages/posts";

type PostsProps = {
  data: any;
  groupBySubtopic?: boolean;
  mode?: "guides" | "news";
};

function getPostDateTimestamp(dateValue?: string | null) {
  if (!dateValue) {
    return Number.NEGATIVE_INFINITY;
  }

  const timestamp = new Date(dateValue).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

function getTextContent(node: any): string {
  if (!node) {
    return "";
  }

  if (typeof node.text === "string") {
    return node.text;
  }

  if (!Array.isArray(node.children)) {
    return "";
  }

  return node.children.map(getTextContent).join(" ");
}

function getFirstArticlePreview(body: any): string {
  const children = body?.children;
  if (!Array.isArray(children)) {
    return "";
  }

  for (const child of children) {
    if (child?.type !== "p") {
      continue;
    }

    const text = getTextContent(child).replace(/\s+/g, " ").trim();
    if (text) {
      return text;
    }
  }

  for (const child of children) {
    const text = getTextContent(child).replace(/\s+/g, " ").trim();
    if (text) {
      return text;
    }
  }

  return "";
}

function isNewsPost(post: any) {
  const categorySlug = post?.category?.slug?.trim()?.toLowerCase();
  const [folderCategory] = post?._sys?.breadcrumbs || [];

  return categorySlug === "news" || folderCategory === "news";
}

function formatSubtopicLabel(subtopic?: string | null) {
  if (!subtopic) {
    return "";
  }

  return subtopic
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildSubtopicHrefMap(sortedPosts: any[], mode: "guides" | "news") {
  const hrefMap = new Map<string, string>();

  sortedPosts.forEach((postData) => {
    const post = postData?.node;
    const subtopic = post?.subtopic?.trim();

    if (!subtopic || !post?.isSubtopicPage || hrefMap.has(subtopic)) {
      return;
    }

    hrefMap.set(subtopic, getPostHref(post, mode));
  });

  return hrefMap;
}

function renderBadge({
  label,
  href,
  tone,
}: {
  label?: string | null;
  href?: string;
  tone: "category" | "subtopic";
}) {
  if (!label) {
    return null;
  }

  const classesByTone = {
    category:
      "inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600 transition-colors",
    subtopic:
      "inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-altblue-600 transition-colors",
  };
  const classes = classesByTone[tone];

  if (!href) {
    return <span className={classes}>{label}</span>;
  }

  return (
    <Link
      href={href}
      locale={false}
      className={`${classes} ${
        tone === "category" ? "hover:bg-slate-200" : "hover:bg-blue-100"
      }`}
    >
      {label}
    </Link>
  );
}

function renderPostCard({
  postData,
  subtopicHrefMap,
  mode,
}: {
  postData: any;
  subtopicHrefMap: Map<string, string>;
  mode: "guides" | "news";
}) {
  const post = postData.node;
  const date = new Date(post.date);
  const previewText = getFirstArticlePreview(post._body);
  let formattedDate = "";

  if (!isNaN(date.getTime())) {
    formattedDate = format(date, "MMM dd, yyyy");
  }

  const postHref = getPostHref(post, mode);
  const showGuideMeta = mode === "guides";
  const categoryLabel = showGuideMeta ? post?.category?.category?.trim() : "";
  const categoryHref =
    showGuideMeta && post?.category?.slug
      ? post.category.slug === "news"
        ? "/news"
        : getTopicHref(post.category.slug)
      : undefined;
  const subtopicLabel = showGuideMeta
    ? formatSubtopicLabel(post?.subtopic)
    : "";
  const mappedSubtopicHref = post?.subtopic
    ? subtopicHrefMap.get(post.subtopic.trim())
    : undefined;
  const subtopicHref =
    mappedSubtopicHref && mappedSubtopicHref !== postHref
      ? mappedSubtopicHref
      : undefined;

  return (
    <article
      key={post._sys.filename}
      className="px-6 overflow-hidden transition-all duration-200 ease-out bg-white border shadow-sm group rounded-xl border-slate-200 py-7 hover:border-slate-300 hover:shadow sm:px-8"
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {formattedDate !== "" && (
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-400">
            {formattedDate}
          </p>
        )}
      </div>
      {(categoryLabel || subtopicLabel) && (
        <div className="flex flex-wrap gap-2 mb-4">
          {renderBadge({
            label: categoryLabel,
            href: categoryHref,
            tone: "category",
          })}
          {renderBadge({
            label: subtopicLabel,
            href: subtopicHref,
            tone: "subtopic",
          })}
        </div>
      )}
      <Link href={postHref} locale={false} className="block">
        <h3 className="mb-4 text-2xl font-bold leading-tight tracking-tight text-slate-800 transition-colors duration-150 ease-out group-hover:text-slate-900 md:text-[2rem]">
          {post.title}
          <span className="inline-block translate-y-[-1px] pl-1 align-middle opacity-0 transition-all duration-300 ease-out group-hover:translate-x-1 group-hover:opacity-100">
            <BsArrowRight className="inline-block w-auto h-6 opacity-70" />
          </span>
        </h3>
        {previewText && (
          <p
            className={`w-full text-base leading-7 ${
              showGuideMeta ? "text-slate-600" : "text-gray-400"
            }`}
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {previewText}
          </p>
        )}
      </Link>
    </article>
  );
}

export default function Posts({
  data,
  groupBySubtopic = false,
  mode = "guides",
}: PostsProps) {
  // const theme = useTheme();
  const sortedPosts = [...(data || [])].sort((left, right) => {
    const leftTimestamp = getPostDateTimestamp(left?.node?.date);
    const rightTimestamp = getPostDateTimestamp(right?.node?.date);

    return rightTimestamp - leftTimestamp;
  });
  const subtopicHrefMap =
    mode === "news"
      ? new Map<string, string>()
      : buildSubtopicHrefMap(sortedPosts, mode);

  if (groupBySubtopic) {
    const groups: Array<{
      key: string;
      posts: any[];
    }> = [];
    const seenGroups = new Map<string, number>();

    sortedPosts.forEach((postData) => {
      const post = postData?.node;
      const subtopic = post?.subtopic?.trim() || "";
      const groupKey = subtopic || `__${post?._sys?.filename}`;
      const existingIndex = seenGroups.get(groupKey);

      if (existingIndex === undefined) {
        seenGroups.set(groupKey, groups.length);
        groups.push({
          key: groupKey,
          posts: [postData],
        });
        return;
      }

      groups[existingIndex].posts.push(postData);
    });

    return (
      <div className="space-y-6">
        {groups.map((group) => (
          <React.Fragment key={group.key}>
            {group.posts.map((postData) =>
              renderPostCard({
                postData,
                subtopicHrefMap,
                mode,
              }),
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedPosts.map((postData) =>
        renderPostCard({
          postData,
          subtopicHrefMap,
          mode,
        }),
      )}
    </div>
  );
}
